import React, { useState } from 'react'
import { Menu, useTheme, useMediaQuery, Divider, Collapse, List } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { StyledGroupName, StyledMenuItem, InstitutionDropdown } from './styles'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { getPagePermissions } from '../../../store/slices/roleSlice/selectors'
import { useAllowedPath } from '../../../hooks/useAllowedPath'
import { type MenuItemProps, adminInstitutionMenuList, adminSettingsMenuList, adminSurveySetupMenuList, type LinkProps } from '../../../consts/TopbarMenuItems'
import { StyledListItemButton, StyledListItemText } from '../MobileTabMenu/style'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ROUTES from '../../../consts/paths'
import { ROLES, ReportRoles } from '../../../consts/roles'
import { useUserProfile } from '../../../hooks/useUserProfile'

const renderMenuList = (
  filteredMenuItems: MenuItemProps[] | undefined,
  currentPath: string,
  handleMenuItemClick: (route: string) => void
): JSX.Element => {
  return (
    <>
      {filteredMenuItems?.map((item: MenuItemProps, index: number) => (
        <React.Fragment key={`${item.group ?? 'links'}-${index}`}>
          {(item?.group != null) &&
            <StyledGroupName tabIndex={1}>
              {item.group}
            </StyledGroupName>}
          {item?.links?.map((link: LinkProps, linkIndex: number) => {
            return (
              <StyledMenuItem
                role='menuitem'
                tabIndex={1}
                active={link?.link === currentPath}
                key={linkIndex}
                onClick={() => { handleMenuItemClick(link.link) }}
              >
                {link.label}
              </StyledMenuItem>
            )
          })}
          {(item?.group != null && filteredMenuItems?.length - 1 !== index) && <Divider />}
        </React.Fragment>
      ))}
    </>
  )
}

const InstitutionLinks = [ROUTES.TERMS, ROUTES.FACULTY_AND_STAFF, ROUTES.REPORTING_HIERARCHY, ROUTES.COURSE_CATALOG]
const SurveySetupLinks = [ROUTES.DISTRIBUTION_SERIES, ROUTES.SURVEYS]
const SettingsLinks = [ROUTES.ATTRIBUTES, ROUTES.ACADEMIC_UNITS, ROUTES.INSTITUTION_SETTINGS, ROUTES.EMAIL, ROUTES.SIS_SYNC_LOGS]

const AdminMenuItems: React.FC = () => {
  const [isInstitutionOpenForMobile, setIsInstitutionOpenForMobile] = useState<boolean>(false)
  const [isSurveySetupOpenForMobile, setIsSurveySetupOpenForMobile] = useState<boolean>(false)
  const [isSettingsOpenForMobile, setIsSettingsOpenForMobile] = useState<boolean>(false)
  const [anchorElSettings, setAnchorElSettings] = useState<null | HTMLElement>(null)
  const [anchorElSurveySetup, setAnchorElSurveySetup] = useState<null | HTMLElement>(null)

  const navigate = useNavigate()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.only('sm'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const currentPath = window.location.pathname
  const isInstitutionActive = InstitutionLinks.some(link => currentPath.includes(link))
  const isSurveySetupActive = SurveySetupLinks.some(link => currentPath.includes(link))
  const isSettingsActive = SettingsLinks.some(link => currentPath.includes(link))
  const pagePermissions = getPagePermissions()
  const canAccessTerms = useAllowedPath(ROUTES.TERMS)
  const canAccessFacultyAndStaff = useAllowedPath(ROUTES.FACULTY_AND_STAFF)
  const canAccessReportingHierarchy = useAllowedPath(ROUTES.REPORTING_HIERARCHY)
  const canAccessCourseCatalog = useAllowedPath(ROUTES.COURSE_CATALOG)
  const canAccessAcademicUnits = useAllowedPath(ROUTES.ACADEMIC_UNITS)
  const canAccessDistributionSeries = useAllowedPath(ROUTES.DISTRIBUTION_SERIES)
  const canAccessSurveys = useAllowedPath(ROUTES.SURVEYS)
  const canAccessInstitutionalSettings = useAllowedPath(ROUTES.INSTITUTION_SETTINGS)
  const canAccessAttributes = useAllowedPath(ROUTES.ATTRIBUTES)
  const canAccessEmail = useAllowedPath(ROUTES.EMAIL)
  const canAccessSisSyncLogs = useAllowedPath(ROUTES.SIS_SYNC_LOGS)

  const handleOpenMenuSettings = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElSettings(event.currentTarget)
  }

  const handleCloseMenuSettings = (): void => {
    setAnchorElSettings(null)
  }

  const handleOpenMenuSurveySetup = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElSurveySetup(event.currentTarget)
  }

  const handleCloseMenuSurveySetup = (): void => {
    setAnchorElSurveySetup(null)
  }

  const handleMenuItemClick = (route: string): void => {
    navigate(route)
    handleCloseMenuSettings()
    handleCloseMenuSurveySetup()
  }

  const canAccessByRoute: Record<string, boolean> = {
    [ROUTES.TERMS]: canAccessTerms,
    [ROUTES.FACULTY_AND_STAFF]: canAccessFacultyAndStaff,
    [ROUTES.REPORTING_HIERARCHY]: canAccessReportingHierarchy,
    [ROUTES.COURSE_CATALOG]: canAccessCourseCatalog,
    [ROUTES.ACADEMIC_UNITS]: canAccessAcademicUnits,
    [ROUTES.DISTRIBUTION_SERIES]: canAccessDistributionSeries,
    [ROUTES.SURVEYS]: canAccessSurveys,
    [ROUTES.INSTITUTION_SETTINGS]: canAccessInstitutionalSettings,
    [ROUTES.ATTRIBUTES]: canAccessAttributes,
    [ROUTES.EMAIL]: canAccessEmail,
    [ROUTES.SIS_SYNC_LOGS]: canAccessSisSyncLogs
  }

  const filterMenuItemsByAccess = (menuItems: MenuItemProps[]): MenuItemProps[] =>
    menuItems.reduce<MenuItemProps[]>((acc, item) => {
      const links = item.links.filter((link) => canAccessByRoute[link.link] ?? false)
      if (links.length > 0) {
        acc.push({ ...item, links })
      }
      return acc
    }, [])

  const filteredInstitutionMenuItems = filterMenuItemsByAccess(adminInstitutionMenuList)
  const filteredSurveySetupMenuItems = filterMenuItemsByAccess(adminSurveySetupMenuList)
  const filteredSettingsMenuItems = filterMenuItemsByAccess(adminSettingsMenuList)

  // Get current user role to determine what to show
  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []

  const currentRoleId = localStorage.getItem('currentRoleId')
  const currentRole = currentUserRoles.find(role => role.roleId === currentRoleId)

  // Show Institution menu for Account Owner and System Manager
  const shouldShowInstitutionMenu = currentRole?.roleName === ROLES.ACCOUNT_OWNER ||
    currentRole?.roleName === ROLES.SYSTEM_MANAGER

  // Show Surveys menu for Account Owner, System Manager, Owner, Editor
  const shouldShowSurveySetupMenu = currentRole?.roleName === ROLES.ACCOUNT_OWNER ||
    currentRole?.roleName === ROLES.SYSTEM_MANAGER ||
    currentRole?.roleName === ReportRoles.Owner ||
    currentRole?.roleName === ROLES.EDITOR

  const shouldShowSettingsMenu = filteredSettingsMenuItems.length > 0

  return (
    <>
      {
        !isMobile && shouldShowSurveySetupMenu && filteredSurveySetupMenuItems?.length > 0 &&
        <>
          {/* Surveys Button and Menu - For Account Owner, System Manager, Owner, Editor */}
          <InstitutionDropdown disabled={!pagePermissions || !filteredSurveySetupMenuItems?.length} isMobile={isMobile} isTablet={isTablet} active={isSurveySetupActive || Boolean(anchorElSurveySetup)}
            onClick={handleOpenMenuSurveySetup}>
            Surveys <ExpandMoreRoundedIcon />
          </InstitutionDropdown>
          <Menu
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            disableScrollLock
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            anchorEl={anchorElSurveySetup} open={Boolean(anchorElSurveySetup)} onClose={handleCloseMenuSurveySetup} elevation={3}>
            {renderMenuList(filteredSurveySetupMenuItems, currentPath, handleMenuItemClick)}
          </Menu>
        </>
      }
      {
        !isMobile && shouldShowInstitutionMenu && filteredInstitutionMenuItems?.length > 0 &&
        <>
          {/* Institution Button and Menu - Only for Account Owner and System Manager */}
          <InstitutionDropdown disabled={!pagePermissions || !filteredInstitutionMenuItems?.length} isMobile={isMobile} isTablet={isTablet} active={isInstitutionActive || Boolean(anchorElSettings)}
            onClick={handleOpenMenuSettings}>
            Institution <ExpandMoreRoundedIcon />
          </InstitutionDropdown>
          <Menu
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            disableScrollLock
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            anchorEl={anchorElSettings} open={Boolean(anchorElSettings)} onClose={handleCloseMenuSettings} elevation={3}>
            {renderMenuList(filteredInstitutionMenuItems, currentPath, handleMenuItemClick)}
          </Menu>
        </>
      }

      {
        isMobile && shouldShowSurveySetupMenu && filteredSurveySetupMenuItems?.length > 0 &&
        <>
          <StyledListItemButton onClick={() => { setIsSurveySetupOpenForMobile(!isSurveySetupOpenForMobile) }} active={isSurveySetupOpenForMobile || isSurveySetupActive} >
            <StyledListItemText primary="Surveys" />
            {isSurveySetupOpenForMobile ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          <Collapse in={isSurveySetupOpenForMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding role='list'>
              {renderMenuList(filteredSurveySetupMenuItems, currentPath, handleMenuItemClick)}
            </List>
          </Collapse>
        </>
      }

      {
        isMobile && shouldShowInstitutionMenu && filteredInstitutionMenuItems?.length > 0 &&
        <>
          <StyledListItemButton onClick={() => { setIsInstitutionOpenForMobile(!isInstitutionOpenForMobile) }} active={isInstitutionOpenForMobile || isInstitutionActive} >
            <StyledListItemText primary="Institution" />
            {isInstitutionOpenForMobile ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          <Collapse in={isInstitutionOpenForMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding role='list'>
              {renderMenuList(filteredInstitutionMenuItems, currentPath, handleMenuItemClick)}
            </List>
          </Collapse>
        </>
      }

      {
        isMobile && shouldShowSettingsMenu &&
        <>
          <StyledListItemButton onClick={() => { setIsSettingsOpenForMobile(!isSettingsOpenForMobile) }} active={isSettingsOpenForMobile || isSettingsActive} >
            <StyledListItemText primary="Settings" />
            {isSettingsOpenForMobile ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          <Collapse in={isSettingsOpenForMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding role='list'>
              {renderMenuList(filteredSettingsMenuItems, currentPath, handleMenuItemClick)}
            </List>
          </Collapse>
        </>
      }

    </>
  )
}

export default AdminMenuItems
